import React from 'react';
import { Link } from 'react-router-dom';
import {
  BROWSE_FACETS,
  edgePresenceFilterHref,
  nodeFlagFilterHref,
  WESTERN_NOTES_EDGE,
} from '../browse/facets';
import { HAS_SVARAS_FLAG } from '../filters/definitions/flagFilter';
import { APP } from '../app/appConfig';

interface Tile { to: string; title: string; subtitle: string; }

const Card: React.FC<Tile> = ({ to, title, subtitle }) => (
  <Link
    to={to}
    className="block rounded-2xl border border-light bg-bg-2/60 px-5 py-4 hover:bg-white/5 hover:border-strong transition-colors"
  >
    <div className="text-h3 font-display font-semibold text-text-primary">{title}</div>
    <div className="text-small text-text-tertiary mt-1">{subtitle}</div>
  </Link>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-h3 font-display font-semibold mb-4">{title}</h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
  </section>
);

const Browse: React.FC = () => {
  const { bhajanBabiesSection, collections } = APP.features;

  return (
    <main id="main" className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
      <h1 className="text-h1 font-display font-semibold mb-8">Browse</h1>

      <Section title="Bhajans">
        <Card to="/bhajans" title="All Bhajans" subtitle="Search and browse the full library" />
      </Section>

      {/*
        "Browse by" carries the facet hubs AND the two notation lists. The
        notation cards are direct /bhajans links with a pre-applied filter, not
        facet hubs with a value list, so they stay out of ALL_FACETS and are
        rendered by hand after the facets.

        The section is NOT gated on BROWSE_FACETS.length any more: the two
        notation cards exist for every tenant, so a zero-facet app still has a
        "Browse by" worth drawing.

        Neither card is gated on the catalog actually containing a match.
        /browse fetches nothing, so gating would mean loading the whole catalog
        just to decide whether to draw a tile, and popping it in after a network
        round-trip. An empty result reads honestly — "0 of N bhajans" with a
        removable pill — and needs no deploy to start working.
      */}
      <Section title="Browse by">
        {BROWSE_FACETS.map((f) => (
          <Card key={f.key} to={`/browse/${f.key}`} title={f.pluralLabel} subtitle={`Bhajans by ${f.label.toLowerCase()}`} />
        ))}
        <Card
          to={nodeFlagFilterHref(HAS_SVARAS_FLAG)}
          title="Bhajans with Svaras"
          subtitle="Bhajans whose lyrics carry svara notation"
        />
        <Card
          to={edgePresenceFilterHref(WESTERN_NOTES_EDGE)}
          title="Bhajans with Western Notes"
          subtitle="Bhajans with a Western staff-notation score"
        />
      </Section>

      {(bhajanBabiesSection || collections) && (
        <Section title="More">
          {bhajanBabiesSection && <Card to="/bhajan-babies" title="Bhajan Babies" subtitle="Animated cartoon bhajans on YouTube" />}
          {collections && <Card to="/collections" title="Collections" subtitle="Curated collections" />}
        </Section>
      )}
    </main>
  );
};

export default Browse;
